import _ from 'lodash';
import moment, { Moment, MomentInput } from 'moment';
import { DETENTION_ALERT_DAYS_OUT } from 'pages/TrackAndTrace/config';
import {
  Appointment,
  Container,
  LegacyContainerStatus,
  PickupAppointment,
  PickupInfo,
  HoldState,
  Hold,
} from 'types/Watchlist';
import { getDateDiff } from 'utils/common';
import { TriggerPickupAppointment } from '../../../../../types/Watchlist';

const getDemurrageFee = (fee: string) => {
  const parsed = parseInt(fee, 10);
  const asNumber = Number.isNaN(parsed) ? 0 : parsed;
  return Math.abs(asNumber);
};

const PICKUP_HOLD_KEYS: { key: keyof PickupInfo; name: string }[] = [
  { key: 'customsHold', name: 'Customs' },
  { key: 'freightHold', name: 'Freight' },
  { key: 'lineHold', name: 'Line' },
  { key: 'tmfHold', name: 'TMF' },
  { key: 'terminalHold', name: 'Terminal' },
  { key: 'ctfHold', name: 'CTF' },
  { key: 'otherHold', name: 'Other holds' },
];

export interface TransformedHold {
  name: string;
  note: null | string;
  state: HoldState;
}

export const displayTerminalPort = (
  terminalName: string | null | undefined,
  portName: string | null | undefined
) => {
  if (_.isNil(terminalName)) {
    return portName;
  }
  if (_.isNil(portName)) {
    return terminalName;
  }
  return `${terminalName} - ${portName}`;
};

export const parsePickupInfo = (pickupInfo: PickupInfo | null) => {
  const holds: TransformedHold[] = [];

  // We want the front end to be agnostic to whether holds have been defined yet, so we'll
  // put empty data if not
  PICKUP_HOLD_KEYS.forEach((hold) => {
    const holdInfo = pickupInfo
      ? (pickupInfo[hold.key] as null | Hold)
      : { raw: null, status: HoldState.Unknown };

    holds.push({
      ...hold,
      note: holdInfo?.raw || null,
      state: holdInfo?.status || HoldState.Unknown,
    });
  });

  if (!pickupInfo)
    return {
      demurrageFeeUsCents: null,
      pickupLastFreeDay: null,
      holds,
      demurrageDue: null,
      demurrageDisplayAlert: false,
      demurrageNumberOfDays: false,
    };

  return {
    demurrageFeeUsCents: pickupInfo.demurragePeriod?.demurrageFeeUsCents
      ? getDemurrageFee(pickupInfo.demurragePeriod?.demurrageFeeUsCents.toString())
      : null,
    pickupLastFreeDay:
      pickupInfo.pickupDates?.lastFreeDay || pickupInfo.pickupDates?.goodThroughDate,
    demurrageDisplayAlert: pickupInfo.demurragePeriod?.displayAlert,
    demurrageNumberOfDays: pickupInfo.demurragePeriod?.numberOfDays,
    holds,
    demurrageDue: pickupInfo.demurragePeriod?.demurrageDue,
    timezone: pickupInfo.timezone,
  };
};

/**
 *
 * @param lastFreeDay
 * @param compareTo the date to compare to if not supplied, now
 * @returns the number of days difference
 */
export function getDeltaLastFreeDay(lastFreeDay: string, compareTo?: MomentInput) {
  const now = compareTo || moment();
  const days = getDateDiff(now, lastFreeDay, 'days', false, true);
  return days;
}

interface NotApplicableAppointment {
  status: 'not-applicable';
}

export interface BookedAppointment {
  status: 'booked';
  startDate: Moment | null;
  endDate: Moment | null;
  timezone: string | null;
}

interface PendingAppointment {
  status: 'pending';
}

export type AppointmentInfo = NotApplicableAppointment | BookedAppointment | PendingAppointment;

export function getAppointmentStatus(
  container: Container,
  appointment: Appointment,
  key: 'pickupBookedAppointment' | 'returnBookedAppointment'
): AppointmentInfo {
  if (
    key === 'pickupBookedAppointment' &&
    container.status !== LegacyContainerStatus.Import &&
    container.status !== LegacyContainerStatus.OnVessel
  ) {
    return { status: 'not-applicable' };
  }

  if (appointment[key]) {
    const appointmentInfo = appointment[key];
    const latestDetails = appointmentInfo?.latestDetails;

    if (
      (key === 'pickupBookedAppointment' && latestDetails?.isImportBooked) ||
      (key === 'returnBookedAppointment' && latestDetails)
    ) {
      const { appointmentStartDate, appointmentEndDate } = latestDetails;

      return {
        status: 'booked',
        startDate: appointmentStartDate
          ? moment(appointmentStartDate).tz(appointmentInfo?.timezone || '')
          : null,
        endDate: appointmentEndDate
          ? moment(appointmentEndDate).tz(appointmentInfo?.timezone || '')
          : null,
        timezone: appointmentInfo ? appointmentInfo.timezone : null,
      };
    }
  }

  return {
    status: 'pending',
  };
}

interface AvailableSlots {
  periodFromDate: Moment;
  periodToDate: Moment;
  slots: unknown[];
  shiftId: string;
}

interface Availability extends Omit<PickupAppointment, 'availableSlots'> {
  availableSlots: AvailableSlots[];
}

export function getPickupAppointmentAvailability(appointment: Appointment): Availability | null {
  const { availablePickupAppointment } = appointment;

  if (availablePickupAppointment && !availablePickupAppointment.scraperError) {
    const availableSlots: AvailableSlots[] = availablePickupAppointment.availableSlots.map((a) => ({
      periodFromDate: moment(a.slotStart).tz(availablePickupAppointment.timezone),
      periodToDate: moment(a.slotEnd).tz(availablePickupAppointment.timezone),
      slots: a.appointmentsAvailable,
      shiftId: a.shiftId,
    }));

    return { ...availablePickupAppointment, availableSlots };
  }

  return null;
}

const PARSERS_TIMEOUT_MINUTES = 3;

export function getPickupAppointmentAvilabilityRefreshing(
  appointment: Appointment,
  triggerPickupAppointment: TriggerPickupAppointment
): boolean {
  let defaultValue = false;

  const {
    lastRefreshedPickupAppointments: lastRefreshedAt,
    lastTriggeredPickupAppointments: lastTriggeredAt,
    pickupBookedAppointment,
    availablePickupAppointment,
  } = appointment;

  if (
    !availablePickupAppointment &&
    !pickupBookedAppointment &&
    !appointment.availablePickupAppointment?.scraperError &&
    triggerPickupAppointment
  ) {
    if (triggerPickupAppointment.pickupAppointments) {
      defaultValue = true;
    }

    if (lastRefreshedAt) {
      const diffInMinutesRefresh = moment().diff(moment(lastRefreshedAt), 'minutes');
      const diffInMinutesTriggger = moment().diff(moment(lastTriggeredAt), 'minutes');
      return diffInMinutesRefresh >= 5 && diffInMinutesTriggger < PARSERS_TIMEOUT_MINUTES;
    }

    const diffInMinutes = moment().diff(moment(lastTriggeredAt), 'minutes');
    return diffInMinutes < PARSERS_TIMEOUT_MINUTES;
  }

  return defaultValue;
}

export function parseGateDates(container: Container) {
  const { gatedIn, gatedOut } = container;

  const gatedOutDate = gatedOut ? moment(gatedOut.occurred).tz(gatedOut.timezone) : null;
  const gatedInDate = gatedIn ? moment(gatedIn.occurred).tz(gatedIn.timezone) : null;
  const days = gatedOutDate ? (gatedInDate || moment()).diff(gatedOutDate, 'days') : null;
  const alert = gatedOut && days !== null ? days >= DETENTION_ALERT_DAYS_OUT : false;

  return { gatedOutDate, gatedInDate, days, alert };
}
